import { useQuery } from '@tanstack/react-query';
import { AlertCircle, RefreshCcw } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { extractApiErrorMessage } from '@/lib/api-error';
import { getAuditLog } from '@/services/api.client';
import type { AuditLogEntry, AuditLogFilters } from '@/types';
import { formatDateTime } from '@/lib/utils';

export function SuperAdminAuditLogPage() {
  const [actionFilter, setActionFilter] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allEntries, setAllEntries] = useState<AuditLogEntry[]>([]);

  const filters: AuditLogFilters = {
    limit: 50,
    ...(actionFilter.length >= 3 ? { action: actionFilter } : {}),
    ...(cursor ? { cursor } : {}),
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'audit-log', filters],
    queryFn: () => getAuditLog(filters),
  });

  // Accumulate entries across pages; reset when filter changes (cursor reset also resets allEntries)
  useEffect(() => {
    if (data?.data) {
      if (!cursor) {
        // First page (or filter reset) — replace
        setAllEntries(data.data);
      } else {
        // Subsequent page — append
        setAllEntries((prev) => [...prev, ...data.data]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const hasMore = data?.meta.hasMore ?? false;
  const nextCursor = data?.meta.nextCursor ?? null;

  const handleFilterChange = (value: string) => {
    setActionFilter(value);
    setCursor(undefined);
    setAllEntries([]);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl text-[var(--text-primary)]">Audit Log</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          Platform-wide audit trail — read-only, append-only
        </p>
      </div>

      <div className="flex gap-3">
        <Input
          placeholder="Filter by action (e.g. ORGANISATION_CREATED)…"
          value={actionFilter}
          onChange={(e) => handleFilterChange(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bg-surface-raised)] text-[var(--text-secondary)]">
                <th className="text-left px-4 py-3 font-medium">Timestamp</th>
                <th className="text-left px-4 py-3 font-medium">Actor</th>
                <th className="text-left px-4 py-3 font-medium">Action</th>
                <th className="text-left px-4 py-3 font-medium">Target</th>
                <th className="text-left px-4 py-3 font-medium">IP</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && allEntries.length === 0
                ? [1, 2, 3].map((i) => (
                    <tr key={i}>
                      <td className="px-4 py-3" colSpan={5}>
                        <Skeleton className="h-4 w-full" />
                      </td>
                    </tr>
                  ))
                : allEntries.map((entry) => (
                    <tr
                      key={entry.id}
                      className={`border-b border-[var(--bg-surface-raised)] last:border-0 ${
                        entry.isImpersonation ? 'bg-[var(--status-amber)]/10' : ''
                      }`}
                    >
                      <td className="px-4 py-3 text-xs text-[var(--text-secondary)] whitespace-nowrap">
                        {formatDateTime(entry.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-[var(--text-primary)]">
                          {entry.actorEmail}
                        </p>
                        <p className="text-xs text-[var(--text-secondary)]">{entry.actorRole}</p>
                        {entry.isImpersonation && (
                          <Badge className="text-[10px] h-4 px-1 mt-0.5 bg-[var(--status-amber)] text-[var(--bg-page)]">
                            impersonation
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-[var(--text-primary)]">
                        {entry.action}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">
                        {entry.targetEntity}
                        {entry.targetId && (
                          <span className="block font-mono text-[var(--text-secondary)] text-[10px]">
                            {entry.targetId}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--text-secondary)] font-mono">
                        {entry.ipAddress ?? '—'}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        {hasMore && nextCursor && (
          <div className="px-4 py-3 border-t border-[var(--bg-surface-raised)]">
            <Button variant="ghost" size="sm" onClick={() => setCursor(nextCursor)}>
              Load more
            </Button>
          </div>
        )}

        {/* Error banner — audit P0: previously a 500 rendered identical to
            "no entries match filters". */}
        {isError && (
          <div
            role="alert"
            className="flex items-start justify-between gap-3 border-t border-[var(--border-error)] bg-[var(--status-red-tint)] px-4 py-3"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-[var(--status-red-text)]" />
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  Couldn&apos;t load the audit log
                </p>
                <p className="text-sm text-[var(--text-secondary)]">
                  {extractApiErrorMessage(error, 'The audit log is unavailable right now.')}
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => void refetch()}>
              <RefreshCcw className="size-3.5" />
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !isError && allEntries.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
            No audit log entries match these filters.
          </p>
        )}
      </div>
    </div>
  );
}
